import { NotificationService } from '@repo/api/notification/notificationService';
import { useEffect } from 'react';

import { useLocalStorage } from '@/hooks/local-storage';
import { WebPushService } from '@/services/web-push';

import type { NotificationsPermission } from '.';

/**
 * React hook for browser push notification permissions and subscriptions.
 * @returns Permission flags and enable/disable methods.
 * @example
 * const { isGranted, enableNotifications } = useManageNotifications();
 */
export const useManageNotifications = () => {
  const isSupported = WebPushService.isSupported();

  const initialPermission = isSupported ? WebPushService.getCurrentPermission() : 'denied';

  const [permission, setPermission] = useLocalStorage<NotificationsPermission>('notificationPermission', initialPermission);

  /**
   * Requests notification permission from the user and updates state.
   * @returns {Promise<NotificationsPermission | null>} The new permission or null if not supported.
   */
  const requestNotificationPermission = async () => {
    if (isSupported) {
      const browserPermission = await WebPushService.requestNotificationPermission();
      setPermission(browserPermission);
      return browserPermission;
    }
    return null;
  };

  /**
   * Enables notifications by requesting permission and subscribing to web push.
   * Updates state and handles errors gracefully.
   */
  const enableNotifications = async () => {
    const currentPermission = WebPushService.getCurrentPermission();
    try {
      const requestPermission = await requestNotificationPermission();
      if (requestPermission === 'granted') {
        let subscription = await WebPushService.getSubscription();
        if (!subscription) {
          subscription = await WebPushService.subscribe();
        }
        await NotificationService.subscribeWebPush({
          subscription: subscription,
        });
      } else {
        setPermission(currentPermission);
      }
    } catch (error) {
      console.error('Notifications Error: ', error);
      setPermission(currentPermission);
    }
  };

  /**
   * Disables notifications by unsubscribing from web push and updating state.
   * Handles errors gracefully.
   */
  const disableNotifications = async () => {
    const currentPermission = WebPushService.getCurrentPermission();
    try {
      setPermission('denied');
      const subscription = await WebPushService.getSubscription();
      const hasUnsubscribed = await WebPushService.unsubscribe();
      if (hasUnsubscribed && subscription) {
        await NotificationService.unsubscribeWebPush({
          subscription: subscription,
        });
      }

      // Clear app badge if supported and permission is denied
      if ('setAppBadge' in navigator) {
        navigator.clearAppBadge();
      }
    } catch (error) {
      console.error('Notifications Error: ', error);
      setPermission(currentPermission);
    }
  };

  useEffect(() => {
    if (isSupported) {
      const browserPermission = WebPushService.getCurrentPermission();
      if (browserPermission !== permission) {
        setPermission(browserPermission);
      }
    }
  }, [isSupported, setPermission]);

  return {
    isGranted: permission === 'granted',
    isDefault: permission === 'default',
    isDenied: permission === 'denied',
    areNotificationsSupported: WebPushService.isSupported(),
    enableNotifications,
    disableNotifications,
  };
};
